import {
  getSeriesValue,
  normalizeChartInputForRendering,
  type T_ChartData,
  type T_ChartDataPoint,
} from "@lattice/core-lib/types/chart";

export type T_ChartValidationResult =
  | { success: true; data: T_ChartData }
  | { success: false; error: string };

function inferSeriesFromData(data: T_ChartData): string[] | undefined {
  const firstPoint = data.data[0];
  if (!firstPoint || typeof firstPoint !== "object") return undefined;
  const seriesKeys = Object.keys(firstPoint).filter(
    (key) => key !== "label" && key !== "value" && key !== "detailLabel",
  );
  return seriesKeys.length > 0 ? seriesKeys : undefined;
}

/**
 * Web mirror of the mobile validateChartData dispatch gate. Accepts raw
 * chart JSON (string) or an already-parsed payload, funnels it through the
 * core-lib historical-normalization boundary, and auto-infers series names.
 *
 * INVARIANT (regression-guarded): normalization must preserve
 * `presentation` — the historical bug dropped it silently and every
 * specialized chart fell through to generic renderers while schema tests
 * stayed green.
 */
export function validateChartData(input: unknown): T_ChartValidationResult {
  let parsed: unknown = input;
  if (typeof input === "string") {
    try {
      parsed = JSON.parse(input);
    } catch (error) {
      return {
        success: false,
        error: `Invalid JSON: ${error instanceof SyntaxError ? error.message : "unparseable"}`,
      };
    }
  }

  const normalized = normalizeChartInputForRendering(parsed);
  if (normalized.kind !== "chart") {
    return { success: false, error: normalized.reason };
  }

  const validated = normalized.chart;
  if (!validated.series) {
    const inferredSeries = inferSeriesFromData(validated);
    if (inferredSeries) validated.series = inferredSeries;
  }

  return { success: true, data: validated };
}

export function getPointLabel(point: T_ChartDataPoint): string {
  const entry = Object.entries(point).find(([key]) => key === "label");
  return typeof entry?.[1] === "string" ? entry[1] : "";
}

/**
 * Long-form point label for header/scan readouts ("Jul 24, 10:20 AM").
 * Falls back to the short axis label when the point carries no detailLabel.
 */
export function getPointDetailLabel(point: T_ChartDataPoint): string {
  const entry = Object.entries(point).find(([key]) => key === "detailLabel");
  return typeof entry?.[1] === "string" ? entry[1] : getPointLabel(point);
}

/** Resolve the numeric series value for a point (single- or multi-series). */
export function getPointValue(
  point: T_ChartDataPoint,
  seriesName: string,
): number | null {
  return getSeriesValue(point, seriesName);
}

/**
 * Scanning fabricated data is forbidden (chart policy): a synthetic
 * two-point fallback line (e.g. a flat placeholder while awaiting history)
 * must not expose scan. Callers pass `syntheticFallback` when they know the
 * data is fabricated; this helper also treats a two-point single-series line
 * with identical values as synthetic.
 */
export function isScanEligible(
  chart: T_ChartData,
  syntheticFallback?: boolean,
): boolean {
  if (syntheticFallback === true) return false;
  if (chart.data.length > 2) return true;
  if (chart.data.length < 2) return false;
  const [first, second] = chart.data;
  if (!first || !second) return false;
  const seriesNames =
    chart.series && chart.series.length > 0 ? chart.series : ["value"];
  return seriesNames.some(
    (name) => getSeriesValue(first, name) !== getSeriesValue(second, name),
  );
}
